import api from './api';

/**
 * Team Service - Provides methods for interacting with team-related endpoints
 */
class TeamService {
  /**
   * Get all teams for an account
   * @param {string} accountId - Account ID
   * @returns {Promise} - API response with team data
   */
  async getTeams(accountId) {
    return await api.get(`account/teams/${accountId}/`);
  }

  /**
   * Get team details by ID
   * @param {string} accountId - Account ID
   * @param {string} teamId - Team ID
   * @returns {Promise} - API response with team details
   */
  async getTeamById(accountId, teamId) {
    return await api.get(`account/teams/${accountId}/${teamId}/`);
  }

  /**
   * Create a new team
   * @param {string} accountId - Account ID
   * @param {Object} teamData - Team data object
   * @param {string} teamData.name - Team name
   * @param {string} [teamData.parent_team] - Parent team ID (optional)
   * @param {string} [teamData.lead] - Team lead user ID (optional)
   * @param {Array} [teamData.members] - Array of user IDs to add as members (optional)
   * @param {Array} [teamData.tags] - Array of tag strings (optional)
   * @returns {Promise} - API response with created team
   */
  async createTeam(accountId, teamData) {
    return await api.post(`account/teams/${accountId}/`, teamData);
  }

  /**
   * Update an existing team
   * @param {string} accountId - Account ID
   * @param {string} teamId - Team ID
   * @param {Object} teamData - Team data to update
   * @returns {Promise} - API response with updated team
   */
  async updateTeam(accountId, teamId, teamData) {
    return await api.patch(`account/team/${accountId}/${teamId}/`, teamData);
  }

  /**
   * Delete a team
   * @param {string} accountId - Account ID
   * @param {string} teamId - Team ID
   * @returns {Promise} - API response
   */
  async deleteTeam(accountId, teamId) {
    return await api.delete(`account/team/${accountId}/${teamId}/`);
  }

  /**
   * Get team members
   * @param {string} accountId - Account ID
   * @param {string} teamId - Team ID
   * @returns {Promise} - API response with team members
   */
  async getTeamMembers(accountId, teamId) {
    return await api.get(`account/teams/${accountId}/${teamId}/members/`);
  }

  /**
   * Add user to team
   * @param {string} accountId - Account ID
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID
   * @returns {Promise} - API response
   */
  async addUserToTeam(accountId, teamId, userId) {
    return await api.post(`account/teams/${accountId}/${teamId}/members/`, {
      user_id: userId
    });
  }

  /**
   * Remove user from team
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID
   * @returns {Promise} - API response
   */
  async removeUserFromTeam(accountId, teamId, userId) {
    return await api.delete(`account/team/${accountId}/${teamId}/member/${userId}/`);
  }

  /**
   * Set team lead
   * @param {string} accountId - Account ID
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID of the team lead
   * @returns {Promise} - API response
   */
  async setTeamLead(accountId, teamId, userId) {
    return await api.patch(`account/team/${accountId}/${teamId}/`, {
      lead: userId
    });
  }

  /**
   * Check if new teams are allowed based on subscription
   * @param {string} accountId - Account ID
   * @returns {Promise} - API response with status and limits
   */
  async checkTeamLimits(accountId) {
    return await api.get(`account/team_limits/${accountId}/`);
  }

  /**
   * Get hierarchical team structure
   * @param {string} accountId - Account ID
   * @returns {Promise} - API response with hierarchical team structure
   */
  async getTeamHierarchy(accountId) {
    const response = await this.getTeams(accountId);
    const teams = response.data;
    
    // Build hierarchical structure
    return this.buildTeamHierarchy(teams);
  }

  /**
   * Helper method to build team hierarchy from flat array
   * @param {Array} teams - Flat array of teams
   * @returns {Array} - Hierarchical team structure
   */
  buildTeamHierarchy(teams) {
    // Create map of teams by ID for quick lookup
    const teamsMap = teams.reduce((map, team) => {
      team.children = [];
      map[team.id] = team;
      return map;
    }, {});
    
    // Create hierarchy
    const rootTeams = [];
    
    teams.forEach(team => {
      if (team.parent_team) {
        const parent = teamsMap[team.parent_team];
        if (parent) {
          parent.children.push(teamsMap[team.id]);
        } else {
          rootTeams.push(teamsMap[team.id]);
        }
      } else {
        rootTeams.push(teamsMap[team.id]);
      }
    });
    
    return rootTeams;
  }

  /**
   * Validate team data against model constraints
   * @param {Object} teamData - Team data to validate
   * @param {Array} existingTeams - Existing teams for uniqueness checks
   * @param {string} [currentTeamId] - Current team ID for update scenarios (optional)
   * @returns {Object} - Validation errors, empty if valid
   */
  validateTeam(teamData, existingTeams, currentTeamId = null) {
    const errors = {};
    
    // Name is required and must be unique
    if (!teamData.name || teamData.name.trim() === '') {
      errors.name = 'Team name is required';
    } else {
      // Check name uniqueness
      const nameExists = existingTeams.some(team => 
        team.id !== currentTeamId && 
        team.name.toLowerCase() === teamData.name.toLowerCase()
      );
      
      if (nameExists) {
        errors.name = 'Team name already exists';
      }
    }
    
    // Prevent circular parent relationship
    if (teamData.parent_team) {
      if (teamData.parent_team === currentTeamId) {
        errors.parent_team = 'A team cannot be its own parent';
      } else {
        // Check for circular references
        let parentId = teamData.parent_team;
        const visited = new Set();
        
        while (parentId) {
          if (visited.has(parentId)) {
            errors.parent_team = 'Circular team hierarchy detected';
            break;
          }
          
          visited.add(parentId);
          const parentTeam = existingTeams.find(team => team.id === parentId);
          parentId = parentTeam ? parentTeam.parent_team : null;
          
          // If the parent chain leads back to the current team
          if (parentId === currentTeamId) {
            errors.parent_team = 'Circular team hierarchy detected';
            break;
          }
        }
      }
    }
    
    return errors;
  }
}

const teamService = new TeamService();
export default teamService;
